import { tools } from '@/modules/Project/Tools/tool-registry'
import { useWorkspaceMembers } from '@/modules/Workspaces/useWorkspaceMembers'
import { useProfileImage } from '@/modules/WorkspaceSettings/useProfileImage'
import { computed, type Ref } from 'vue'
import type { Field, FieldValueInfo } from './Fields/types'

/**
 * Provides extended extended information on tool field's corrected data.
 *
 * - whether it's been corrected
 * - by whom
 *
 * The naming of the composable is intentionally similar to
 * useFieldStatusIndicator to indicate we want to join them together.
 *
 * However, the implementation is completely separate, mainly because we're pulling user info
 * from a different pinia store.
 *
 * If user info were to be attached to the field at serialization,
 * we'd be able to simplify into a basic computed.
 */
export const useFieldStatusIndicatorExtended = (field: Ref<Field | undefined>) => {
  const workspaceMembersStore = useWorkspaceMembers()

  const userProfileImage = useProfileImage(computed(() => field?.value?.manualValueUpdatedBy ?? ''))

  const valueInfo = computed<FieldValueInfo | undefined>(() => {
    const f = field.value
    if (!f) return undefined
    const tool = f.toolValueUpdatedBy
    const manualUserId = f.manualValueUpdatedBy
    const hasManual = f.manualValue !== null && manualUserId
    if (hasManual) {
      // toolWithCorrection
      const member = workspaceMembersStore.getMember(manualUserId ?? '')
      const userAvatarUrl = userProfileImage.value
      const userName =
        member?.firstName || member?.lastName
          ? [member.firstName, member.lastName].filter(Boolean).join(' ')
          : member?.email || 'Unknown user'
      return tool
        ? {
            type: 'toolWithCorrection',
            userName,
            userAvatarUrl,
            toolLabel: tools[tool].label,
            toolIcon: (tool && tools[tool]?.icon) || 'info',
          }
        : { type: 'user', userName, userAvatarUrl }
    }

    if (tool) {
      return { type: 'tool', toolLabel: tools[tool].label, toolIcon: tools[tool].icon }
    }

    return undefined
  })

  return { valueInfo }
}
